// ProfileContext — manages the authenticated user's profile data and preferences.
// This sits between AuthContext (who is logged in) and the rest of the app (what they prefer).
// It handles: fetching/updating the Profiles table row, profile picture uploads via Supabase
// Storage, and exposing convenience fields (currency, defaultView, etc.) to any component.
//
// Usage: wrap your tree with <ProfileProvider>, then call useProfile() in any child.

import { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { supabase } from '../supabase/supabaseClient';
import { useAuth } from './auth.context';

const ProfileContext = createContext();

export const ProfileProvider = ({ children }) => {
  // user comes from AuthContext — it's the Supabase auth session object (or null if logged out)
  const { user } = useAuth();

  // profile — the raw row from the Supabase `Profiles` table for this user
  const [profile, setProfile] = useState(null);

  // loading — true until the first fetch completes; useful for preventing flash of empty UI
  const [loading, setLoading] = useState(true);

  // profilePicSignedUrl — a temporary, expiring URL for displaying the profile picture.
  // We store only the file path in the DB; signed URLs are generated on demand (see effect below).
  const [profilePicSignedUrl, setProfilePicSignedUrl] = useState(null);

  // useCallback gives fetchProfile a stable reference, so the effect below only re-runs
  // when `user` actually changes (login/logout), not on every render.
  const fetchProfile = useCallback(async () => {
    if (!user) {
      // User logged out — clear profile and stop loading
      setProfile(null);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('Profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error && error.code === 'PGRST116') {
      // PGRST116 = "no rows returned" from PostgREST (Supabase's query layer).
      // This shouldn't happen normally — a DB trigger creates a profile row on signup —
      // but if the trigger failed, we create the row here as a safety net.
      const { data: newProfile } = await supabase
        .from('Profiles')
        .insert({ id: user.id, display_name: user.user_metadata?.name ?? null })
        .select()
        .single();
      setProfile(newProfile);
    } else if (data) {
      setProfile(data);
    }

    setLoading(false);
  }, [user]); // re-creates fetchProfile only when user changes

  // Kick off a profile fetch whenever fetchProfile changes (i.e. when user logs in or out)
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Whenever the stored profile picture path changes, generate a fresh signed URL.
  // Supabase Storage doesn't serve files publicly — signed URLs expire after 1 hour (3600s).
  // Storing the path (not the full URL) in the DB keeps things clean and lets us re-sign as needed.
  useEffect(() => {
    if (!profile?.profile_pic) { setProfilePicSignedUrl(null); return; }
    supabase.storage.from('profile_pics')
      .createSignedUrl(profile.profile_pic, 3600)
      .then(({ data }) => { if (data) setProfilePicSignedUrl(data.signedUrl); });
  }, [profile?.profile_pic]);

  // Optimistic update: immediately apply changes to local state so the UI feels instant,
  // then persist to Supabase. On error, refetch to roll back to the real server state.
  const updateProfile = async (updates) => {
    setProfile(prev => ({ ...prev, ...updates })); // optimistic local update

    const { data, error } = await supabase
      .from('Profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();

    if (error) {
      await fetchProfile(); // rollback — restore actual server state
      throw error;
    }

    setProfile(data); // replace optimistic state with confirmed server response
    return data;
  };

  
  // Convenience accessors — pre-extract common fields with sensible defaults so consumers
  // don't need to write `profile?.currency ?? 'GBP'` everywhere they use these values.
  const displayName = profile?.display_name ?? null;
  const email = profile?.email ?? null;
  const currency = profile?.currency ?? 'GBP';       // default currency for formatting
  const defaultView = profile?.default_view ?? 'tiles'; // 'tiles' or 'tables' on the networth page
  const birthYear = profile?.birth_year ?? null;
  const locale = profile?.locale ?? 'en-GB';
  const onboardingCompleted = profile?.onboarding_completed ?? false;
  const targetNetworth = profile?.target_networth ?? 500000; // net worth goal — the target line on the projection chart
  const targetAge = profile?.target_age ?? 65;               // target retirement age; with birthYear gives the target year



  // Uploads a new profile picture to Supabase Storage and saves the path to the DB.
  // Path format: `{userId}/{timestamp}.{ext}` — scoped per user, unique per upload.
  // upsert: true means re-uploading to the same path replaces the file rather than erroring.
  const uploadProfilePic = async (file) => {
    const ext = file.name.split('.').pop();
    const path = `${user.id}/${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('profile_pics')
      .upload(path, file, { upsert: true });

    if (uploadError) throw uploadError;

    await updateProfile({ profile_pic: path }); // save path to DB

    // Generate a signed URL immediately so the new picture shows without waiting for the effect
    const { data } = await supabase.storage.from('profile_pics').createSignedUrl(path, 3600);
    if (data) setProfilePicSignedUrl(data.signedUrl);
  };

  // Removes the profile picture from Storage and clears the path in the DB
  const removeProfilePic = async () => {
    if (!profile?.profile_pic) return;
    await supabase.storage.from('profile_pics').remove([profile.profile_pic]);
    await updateProfile({ profile_pic: null });
  };

  // Thin wrapper around updateProfile for the common case of changing just the display name
  const setDisplayName = async (newDisplayName) => { return updateProfile({ display_name: newDisplayName }); };
  const setBirthYear = async (year) => { return updateProfile({ birth_year: year }); };



  const value = {
    profile,              // full raw profile row — use sparingly; prefer the accessors below
    loading,              // true during initial fetch
    displayName,          // profile?.display_name
    email,                // profile?.email
    currency,             // profile?.currency ?? 'GBP'
    defaultView,          // profile?.default_view ?? 'tiles'
    birthYear,            // profile?.birth_year
    locale,               // profile?.locale ?? 'en-GB'
    onboardingCompleted,  // profile?.onboarding_completed ?? false
    targetNetworth,       // profile?.target_networth ?? 500000
    targetAge,            // profile?.target_age ?? 65
    profilePicSignedUrl,  // temporary display URL for the profile picture (expires in 1h)
    setDisplayName,
    setBirthYear,
    updateProfile,
    uploadProfilePic,
    removeProfilePic,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

// useProfile — the hook consumers call to access profile data and actions.
// Throws a clear error if called outside of <ProfileProvider> rather than silently returning undefined.
export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return context;
};
